import { LineStyleValue } from "constants/chart.constants";
import { MultiProductFeature } from "types/charts/feature";

/**
 * Resolves the line style a product's series should draw with, respecting the
 * "Override Bin Colours" toggle on the MPC.
 *
 * When overrideBinColors is OFF (or multiProduct is absent) → the persisted
 * per-product style (productLineStyles), falling back to the caller's
 * auto-assigned style.
 *
 * When overrideBinColors is ON → "solid" for every product: the per-product
 * colour already identifies the series, so a second visual encoding is noise.
 * The exception is a style the user picked *while* the override was on — those
 * live in overrideLineStyles and beat the solid default (EVA-56).
 *
 * Only LINE STYLE is decided here. Colour lives in getEffectiveSeriesColor.
 */
export function getEffectiveProductLineStyle(
  product: string,
  multiProduct: Maybe<Partial<MultiProductFeature>>,
  fallback: LineStyleValue
): LineStyleValue {
  const properties = multiProduct?.properties;

  // The casts are forced by the MAP type, not by any untyped caller: the feature
  // stores styles as Record<string, string | number[]> while this returns the
  // narrower LineStyleValue ("solid" | number[]) that the pickers require. Every
  // value written to those maps comes from MULTI_PRODUCT_LINE_STYLES, so the
  // narrowing holds. Tightening the two Records would retire these — see EVA-56
  // review thread; deliberately left out of that PR's scope.
  if (properties?.overrideBinColors) {
    return (properties.overrideLineStyles?.[product] as LineStyleValue) ?? "solid";
  }

  return (properties?.productLineStyles?.[product] as LineStyleValue) ?? fallback;
}
